import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpen, FileText, PlayCircle, Sparkles } from "lucide-react";
import { API, imgUrl } from "@/config";
import { CS_TEL_HREF, CS_SMS_HREF, CS_PHONE_DISPLAY, AUTO_LABEL } from "@/brand";

const md = {
  h1: ({ node, ...p }) => <h2 className="font-serif text-2xl mt-8 mb-3 text-[#F6F1E4]" {...p} />,
  h2: ({ node, ...p }) => <h2 className="font-serif text-2xl mt-8 mb-3 text-[#F6F1E4]" {...p} />,
  h3: ({ node, ...p }) => <h3 className="font-serif text-xl mt-6 mb-2 text-[#FFD166]" {...p} />,
  p: ({ node, ...p }) => <p className="text-[#E4E9EF] text-base leading-relaxed mb-4" {...p} />,
  a: ({ node, ...p }) => <a className="text-[#FFD166] underline" target="_blank" rel="noreferrer" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-5 my-4 space-y-2 text-[#E4E9EF] marker:text-[#FFD166]" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-5 my-4 space-y-2 text-[#E4E9EF] marker:text-[#FFD166]" {...p} />,
  li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
  strong: ({ node, ...p }) => <strong className="text-[#F6F1E4] font-semibold" {...p} />,
  img: ({ node, ...p }) => <img className="rounded-xl my-4 max-h-80 mx-auto" alt="" {...p} />,
};

const embedUrl = (url) => {
  if (!url) return "";
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return "";
};

const GuideBody = ({ item }) => {
  const embed = embedUrl(item.guide_video_url);
  const empty = !item.guide_markdown && !item.guide_video_url && !item.guide_pdf_url;
  return (
    <div data-testid={`guide-body-${item.id}`}>
      {embed && (
        <div className="aspect-video rounded-xl overflow-hidden border border-[#FFD166]/20 mb-5" data-testid={`guide-video-${item.id}`}>
          <iframe src={embed} title={`${item.name} how-to video`} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      )}
      {item.guide_video_url && !embed && (
        <a href={item.guide_video_url} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-2 mb-5 text-sm" data-testid={`guide-video-link-${item.id}`}><PlayCircle className="w-4 h-4" /> Watch the how-to video</a>
      )}
      {item.guide_markdown ? (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>{item.guide_markdown}</ReactMarkdown>
      ) : (
        (item.features || []).length > 0 && (
          <ul className="list-disc pl-5 my-4 space-y-2 text-[#E4E9EF] marker:text-[#FFD166]" data-testid={`guide-features-${item.id}`}>
            {item.features.map((f) => <li key={f}>{f}</li>)}
          </ul>
        )
      )}
      {item.guide_pdf_url && (
        <a href={item.guide_pdf_url} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-2 mt-3 text-sm" data-testid={`guide-pdf-${item.id}`}><FileText className="w-4 h-4" /> Manufacturer manual (PDF)</a>
      )}
      {empty && (
        <p className="text-sm text-[#B9C8D5] italic" data-testid={`guide-empty-${item.id}`}>We're still writing this guide. Call or text us and we'll walk you through it.</p>
      )}
    </div>
  );
};

export default function ProductGuide() {
  const { productId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/guides/${productId}`).then((r) => setData(r.data)).catch(() => setError("We couldn't find a guide for that item."));
  }, [productId]);

  return (
    <div className="min-h-screen bg-[#040A0F] text-[#F6F1E4]">
      <Helmet><title>{`${data ? `${data.name} — How-to guide` : "How-to guide"} · Enchanted Gear Rentals`}</title></Helmet>
      <div className="max-w-2xl mx-auto px-5 py-8" data-testid="product-guide-page">
        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FFD166]"><Sparkles className="w-3.5 h-3.5" /> Enchanted Gear Rentals</Link>
        {error && <p className="mt-10 text-center text-[#B9C8D5]" data-testid="guide-error">{error}</p>}
        {data && (
          <>
            <div className="mt-6 flex items-center gap-4">
              {data.image && <img src={imgUrl(data.image)} alt={data.name} className="w-20 h-20 rounded-xl object-contain bg-black/30 p-1 border border-[#FFD166]/20" />}
              <div>
                <p className="eyebrow text-[#FFD166] flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> How-to guide</p>
                <h1 className="font-serif text-3xl sm:text-4xl leading-tight" data-testid="guide-title">{data.name}</h1>
              </div>
            </div>

            <div className="mt-6 glass p-5">
              {data.is_bundle && data.components.length > 0 ? (
                <div className="space-y-8">
                  {data.guide_markdown && <GuideBody item={{ ...data, features: [] }} />}
                  {data.components.map((c) => (
                    <div key={c.id} className="border-t border-[#FFD166]/15 pt-6 first:border-0 first:pt-0" data-testid={`guide-component-${c.id}`}>
                      <h2 className="font-serif text-2xl text-[#FFD166] mb-3">{c.name}</h2>
                      <GuideBody item={c} />
                    </div>
                  ))}
                </div>
              ) : (
                <GuideBody item={data} />
              )}
            </div>

            <div className="mt-6 rounded-xl border border-[#FFD166]/20 bg-black/30 p-4 text-center text-sm" data-testid="guide-contact">
              <div>Need a hand? Call or text us at <a href={CS_TEL_HREF} className="text-[#FFD166] font-medium">{CS_PHONE_DISPLAY}</a></div>
              <a href={CS_SMS_HREF} className="btn-primary inline-block mt-3 !py-2 !px-5 text-sm" data-testid="guide-text-us">Text us</a>
              <div className="text-[10px] text-[#B9C8D5] mt-3">{AUTO_LABEL}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
